import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessIdea } from '../../../services/businessIdeaTypes';
import { BusinessPlan as BusinessPlanType } from '../../../types/businessPlan';

interface BusinessPlanCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tabId: string) => void;
  savedPlans: BusinessPlanType[];
  savedIdeas: BusinessIdea[];
}

interface Command {
  id: string;
  title: string;
  description: string;
  icon: string;
  action: () => void;
  category: 'navigation' | 'action' | 'plan' | 'idea';
}

const BusinessPlanCommandPalette: React.FC<BusinessPlanCommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate,
  savedPlans,
  savedIdeas
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Build commands list
  const commands: Command[] = [
    // Navigation
    {
      id: 'nav-plans',
      title: 'Go to My Plans',
      description: 'View all your business plans',
      icon: '📄',
      action: () => onNavigate('plans'),
      category: 'navigation'
    },
    {
      id: 'nav-ideas',
      title: 'Go to Ideas',
      description: 'Browse your business ideas',
      icon: '💡',
      action: () => onNavigate('ideas'),
      category: 'navigation'
    },
    {
      id: 'nav-templates',
      title: 'Go to Templates',
      description: 'Explore industry templates',
      icon: '📋',
      action: () => onNavigate('templates'),
      category: 'navigation'
    },
    // Actions
    {
      id: 'action-new-plan',
      title: 'Create New Plan',
      description: 'Start a new business plan',
      icon: '✨',
      action: () => {
        navigate('/business-plan/new');
        onClose();
      },
      category: 'action'
    },
    {
      id: 'action-import',
      title: 'Import Plan',
      description: 'Import an existing business plan',
      icon: '📥',
      action: () => {
        // TODO: Implement import
        onClose();
      },
      category: 'action'
    },
    // Recent Plans
    ...savedPlans.slice(0, 5).map(plan => ({
      id: `plan-${plan.id}`,
      title: plan.title || 'Untitled Plan',
      description: `Last updated ${new Date(plan.updated_at || '').toLocaleDateString()}`,
      icon: '📄',
      action: () => {
        navigate(`/business-plan/${plan.id}`);
        onClose();
      },
      category: 'plan' as const
    })),
    // Recent Ideas
    ...savedIdeas.slice(0, 5).map(idea => ({
      id: `idea-${idea.id}`,
      title: idea.title,
      description: idea.description?.substring(0, 60) + '...' || '',
      icon: '💡',
      action: () => {
        navigate('/business-plan/new', { state: { selectedIdea: idea } });
        onClose();
      },
      category: 'idea' as const
    }))
  ];

  // Filter commands based on search
  const filteredCommands = search
    ? commands.filter(cmd =>
        cmd.title.toLowerCase().includes(search.toLowerCase()) ||
        cmd.description.toLowerCase().includes(search.toLowerCase())
      )
    : commands;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => (i + 1) % filteredCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => (i - 1 + filteredCommands.length) % filteredCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        filteredCommands[selectedIndex]?.action();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, onClose]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Command Palette */}
      <div className="flex min-h-screen items-start justify-center p-4 pt-[10vh]">
        <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl ring-1 ring-black/5">
          {/* Search Input */}
          <div className="border-b border-gray-200 px-4">
            <div className="flex items-center gap-3 py-3">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search plans, ideas, or actions..."
                className="flex-1 border-0 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
              />
              <kbd className="hidden sm:inline-flex items-center rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-500">
                ESC
              </kbd>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {filteredCommands.length === 0 ? (
              <div className="py-12 text-center">
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-sm text-gray-500">No results found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredCommands.map((command, index) => (
                  <button
                    key={command.id}
                    onClick={() => {
                      command.action();
                      onClose();
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`
                      w-full rounded-lg px-4 py-3 text-left transition
                      ${index === selectedIndex
                        ? 'bg-blue-50 ring-2 ring-blue-500'
                        : 'hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{command.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{command.title}</div>
                        <div className="text-sm text-gray-500 truncate">{command.description}</div>
                      </div>
                      {command.category === 'navigation' && (
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↑</kbd>
                  <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↓</kbd>
                  <span>Navigate</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">↵</kbd>
                  <span>Select</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5">ESC</kbd>
                <span>Close</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanCommandPalette;
